/**
 * Rental-agnostic date controls for admin surfaces.
 *
 * Dates intentionally remain strings.  Constructing a Date from a YYYY-MM-DD
 * string is UTC-dependent and changes the displayed day in several timezones.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type LocalDate = string;
export interface DateRangeValue { from: LocalDate; to: LocalDate; }
export interface AdminDateBaseProps {
  label?: string;
  ariaLabel?: string;
  min?: LocalDate;
  max?: LocalDate;
  disabled?: boolean;
  error?: string;
  hint?: string;
  optional?: boolean;
  id?: string;
  className?: string;
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const todayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const parts = (s: string) => s.match(/^(\d{4})-(\d{2})-(\d{2})$/)?.slice(1).map(Number);
const dateFromParts = (s: string) => {
  const p = parts(s);
  return p ? new Date(p[0], p[1] - 1, p[2]) : new Date();
};
const dateString = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const moveDay = (s: string, amount: number) => {
  const d = dateFromParts(s || todayString());
  d.setDate(d.getDate() + amount);
  return dateString(d);
};
const monthLabel = (year: number, month: number) =>
  new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(new Date(year, month, 1));

function Calendar({ value, onSelect, min, max, rangeEnd, label }: {
  value: string; onSelect: (value: string) => void; min?: string; max?: string;
  rangeEnd?: string; label: string;
}) {
  const seed = dateFromParts(value || min || todayString());
  const [month, setMonth] = useState({ year: seed.getFullYear(), month: seed.getMonth() });
  useEffect(() => {
    if (value) {
      const d = dateFromParts(value);
      setMonth({ year: d.getFullYear(), month: d.getMonth() });
    }
  }, [value]);
  const cells = useMemo(() => {
    const first = new Date(month.year, month.month, 1).getDay();
    const count = new Date(month.year, month.month + 1, 0).getDate();
    return Array.from({ length: first + count }, (_, i) => i < first ? "" : `${month.year}-${String(month.month + 1).padStart(2, "0")}-${String(i - first + 1).padStart(2, "0")}`);
  }, [month]);
  const changeMonth = (delta: number) => setMonth(({ year, month: m }) => {
    const d = new Date(year, m + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  return (
    <div role="dialog" aria-label={label} className="w-[min(19rem,calc(100vw-2rem))] rounded-xl border border-border bg-background p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" data-testid="button-date-previous-month" aria-label="Previous month" onClick={() => changeMonth(-1)} className="rounded-md p-1 hover:bg-muted"><ChevronLeft className="h-4 w-4" /></button>
        <strong data-testid="text-date-month">{monthLabel(month.year, month.month)}</strong>
        <button type="button" data-testid="button-date-next-month" aria-label="Next month" onClick={() => changeMonth(1)} className="rounded-md p-1 hover:bg-muted"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs text-muted-foreground">{DAYS.map(day => <span key={day} className="py-1">{day}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, index) => {
          const disabled = !day || (!!min && day < min) || (!!max && day > max);
          const selected = day === value || day === rangeEnd;
          const inRange = !!rangeEnd && !!value && day > value && day < rangeEnd;
          return day ? <button key={day} type="button" data-testid={`button-date-day-${day}`} aria-label={day} aria-current={day === todayString() ? "date" : undefined} disabled={disabled} onClick={() => onSelect(day)} className={cn("h-8 rounded-md text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-35", selected && "bg-primary text-primary-foreground hover:bg-primary", inRange && "bg-primary/10", day === todayString() && !selected && "font-bold text-primary")}>{Number(day.slice(-2))}</button> : <span key={`empty-${index}`} />; 
        })}
      </div>
    </div>
  );
}

function FieldShell({ id, label, error, hint, optional, className, children }: AdminDateBaseProps & { children: React.ReactNode }) {
  const errorId = `${id}-error`;
  return <div className={cn("space-y-1.5", className)}>
    {label && <label htmlFor={id} className="block text-sm font-medium">{label}{optional && <span className="ml-1 font-normal text-muted-foreground">(optional)</span>}</label>}
    {children}
    {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p id={errorId} role="alert" data-testid={`error-${id}`} className="text-xs text-destructive">{error}</p>}
  </div>;
}

export interface AdminDatePickerProps extends AdminDateBaseProps { value: LocalDate; onChange: (value: LocalDate) => void; }
export function AdminDatePicker({ value, onChange, label = "Date", ariaLabel, id: suppliedId, ...props }: AdminDatePickerProps) {
  const generated = useId(); const id = suppliedId ?? `admin-date-${generated}`; const [open, setOpen] = useState(false); const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", close); return () => document.removeEventListener("mousedown", close); }, []);
  const setDate = (next: string) => { onChange(next); setOpen(false); };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const amount = e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : 0;
    if (amount) {
      e.preventDefault();
      const next = moveDay(value || todayString(), amount);
      if ((!props.min || next >= props.min) && (!props.max || next <= props.max)) onChange(next);
    }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(v => !v); }
  };
  return <FieldShell id={id} label={label} {...props}><div ref={ref} className="relative flex items-center">
    <CalendarDays aria-hidden="true" className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
    <input id={id} data-testid={`input-${id}`} type="text" inputMode="none" readOnly value={value} placeholder="YYYY-MM-DD" disabled={props.disabled} min={props.min} max={props.max} aria-label={ariaLabel || label || "Date"} aria-invalid={!!props.error} aria-describedby={props.error ? `${id}-error` : undefined} onClick={() => !props.disabled && setOpen(v => !v)} onKeyDown={onKeyDown} className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50" />
    {value && !props.disabled && <button type="button" data-testid={`button-clear-${id}`} aria-label={`Clear ${ariaLabel || label || "date"}`} onClick={() => onChange("")} className="absolute right-2 rounded p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>}
    {open && <div className="absolute z-50 top-full mt-2 left-0"><Calendar value={value} min={props.min} max={props.max} label={`${ariaLabel || label || "Date"} calendar`} onSelect={setDate} /></div>}
  </div></FieldShell>;
}

export interface AdminDateRangePickerProps extends Omit<AdminDateBaseProps, "id"> { value: DateRangeValue; onChange: (value: DateRangeValue) => void; fromLabel?: string; toLabel?: string; }
export function AdminDateRangePicker({ value, onChange, label = "Date range", fromLabel = "Start date", toLabel = "End date", ...props }: AdminDateRangePickerProps) {
  const [active, setActive] = useState<"from" | "to">("from"); const [open, setOpen] = useState(false); const id = useId(); const fromId = `${id}-from`; const toId = `${id}-to`;
  const invalidOrder = !!value.from && !!value.to && value.to < value.from;
  const error = props.error ?? (invalidOrder ? "End date must be on or after start date." : undefined);
  const select = (next: string) => { const nextValue = active === "from" ? { from: next, to: value.to } : { from: value.from, to: next }; onChange(nextValue); if (active === "from") setActive("to"); else setOpen(false); };
  const handleFieldKeyDown = (which: "from" | "to", current: string) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    const amount = e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : 0;
    if (amount) {
      e.preventDefault();
      const next = moveDay(current || todayString(), amount);
      if ((!props.min || next >= props.min) && (!props.max || next <= props.max)) onChange({ ...value, [which]: next });
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActive(which);
      setOpen(open => !open);
    }
    if (e.key === "Escape") setOpen(false);
  };
  return <FieldShell id={fromId} label={label} error={error} hint={props.hint} optional={props.optional} className={props.className}><div className="flex flex-col gap-2 sm:flex-row sm:items-center">
    {[["from", fromId, fromLabel, value.from], ["to", toId, toLabel, value.to]].map(([which, fieldId, fieldLabel, fieldValue]) => <div key={which as string} className="relative flex-1"><label htmlFor={fieldId as string} className="sr-only">{fieldLabel as string}</label><input id={fieldId as string} data-testid={`input-${which}-date`} type="text" inputMode="none" readOnly value={fieldValue as string} placeholder="YYYY-MM-DD" disabled={props.disabled} aria-invalid={!!error} aria-describedby={error ? `${fromId}-error` : undefined} onClick={() => { setActive(which as "from" | "to"); setOpen(true); }} onKeyDown={handleFieldKeyDown(which as "from" | "to", fieldValue as string)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50" />{fieldValue && <button type="button" data-testid={`button-clear-${which}-date`} aria-label={`Clear ${fieldLabel}`} onClick={() => onChange({ ...value, [which]: "" })} className="absolute right-2 top-1.5 rounded p-1 text-muted-foreground"><X className="h-4 w-4" /></button>}</div>)}
  </div>{open && <div className="relative z-50 mt-2"><Calendar value={active === "from" ? value.from : value.to} rangeEnd={active === "from" ? value.to : value.from} min={props.min} max={props.max} label={`${label} calendar`} onSelect={select} /></div>}</FieldShell>;
}

export interface AdminDateTimePickerProps extends AdminDateBaseProps { value: string; onChange: (value: string) => void; }
export function AdminDateTimePicker({ value, onChange, label = "Date and time", id: suppliedId, ...props }: AdminDateTimePickerProps) {
  const generated = useId(); const id = suppliedId ?? `admin-datetime-${generated}`;
  const [date = "", time = ""] = value.split("T");
  const updateDate = (nextDate: string) => onChange(nextDate ? `${nextDate}T${time || "00:00"}` : "");
  const updateTime = (nextTime: string) => {
    if (date) onChange(`${date}T${nextTime}`);
  };
  return <FieldShell id={id} label={label} {...props}>
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <AdminDatePicker
        id={id}
        label=""
        ariaLabel={`${label} date`}
        value={date}
        onChange={updateDate}
        min={props.min}
        max={props.max}
        disabled={props.disabled}
        className="flex-1"
      />
      <input
        id={`${id}-time`}
        data-testid={`input-${id}-time`}
        type="time"
        aria-label={`${label} time`}
        value={time}
        disabled={props.disabled || !date}
        aria-invalid={!!props.error}
        aria-describedby={props.error ? `${id}-error` : undefined}
        onChange={e => updateTime(e.target.value)}
        className="min-w-32 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  </FieldShell>;
}

export { AdminDatePicker as DatePicker, AdminDateRangePicker as DateRangePicker, AdminDateTimePicker as DateTimePicker };